import React, {
  ReactElement,
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import cn from 'classnames';

import { Spinner } from 'components';
import Icon from 'components/Icon';
import { getLayout } from 'components/layouts/Sidebar';

import { Row, Col } from 'components/layout';
import StudentSocietyFilters from 'components/StudentSocietyFilters';
import { Input } from 'components/form';
import { Avatar } from 'components/Avatar/Avatar.component';
import { ButtonV3 } from 'components/ComponentV2';
import { useStudentSocieties } from 'hooks/useStudentSocieties';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { UniversityAttributes } from 'lib/models/university';
import { parseResponse } from 'lib/utils/parser';
import { withAuth } from 'lib/hocs/withAuth';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useApp } from 'lib/contexts/save-in-app-context';
import { useAuth } from 'lib/providers/AuthProvider';

import styles from './StudentSocieties.module.scss';

const StudentSocieties = (): ReactElement => {
  const router = useRouter();
  const { isRecruiter } = useAuth();

  const [isFilterClicked, setFilterClicked] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const useBottomRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);

  const {
    societies,
    isLoading,
    fetchMoreSocieties,
    hasMoreRecords,
    search,
    setSearch,
    minimumMembersOptions,
    minimumEventsOptions,
    sinceMonthsOptions,
    filters,
    partialFilters,
    onChangeFilters,
    partialCount,
    partialLoading,
  } = useStudentSocieties();
  const { societySearch, setSocietySearch } = useApp();
  const searchRef = useRef(search);

  useEffect(() => {
    if (societySearch !== null) {
      setSearch(societySearch);
    }
  }, [societySearch]);

  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  useEffect(() => {
    return () => {
      setSocietySearch(searchRef.current);
    };
  }, []);

  useVisibleOnIntersect({
    elRef: useBottomRef,
    threshold: 0,
    onIntersect: isLoading ? undefined : fetchMoreSocieties,
    rootMargin: '250px',
  });

  const onHeaderShow = useCallback(() => {
    setHeaderScrolled(false);
  }, []);

  const onHeaderHide = useCallback(() => {
    setHeaderScrolled(true);
  }, []);

  useVisibleOnIntersect({
    elRef: headerRef,
    threshold: 1,
    onIntersect: onHeaderShow,
    onHidden: onHeaderHide,
    rootMargin: '0px',
    isEqualToThreshold: true,
  });

  const searchBox = useMemo(() => {
    return (
      <Input
        inputClassName={styles.StudentSocietiesSearch}
        placeholder="Search"
        startIcon="search"
        startIconSize="auto"
        startIconClassName={styles.StudentSocietiesSearchIcon}
        endIconSize="xxsmall"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        endIcon={search ? 'cancel' : undefined}
        onEndIconClick={() => setSearch('')}
      />
    );
  }, [search]);

  const filterRow = (
    <div
      className={cn(styles['filter-container'], {
        [styles['filter-container-scrolled']]: headerScrolled,
      })}
    >
      <StudentSocietyFilters
        headerScrolled={headerScrolled}
        isFilterClicked={isFilterClicked}
        isRecruiter={isRecruiter}
        searchBox={searchBox}
        minimumMembersOptions={minimumMembersOptions}
        minimumEventsOptions={minimumEventsOptions}
        sinceMonthsOptions={sinceMonthsOptions}
        filters={filters}
        partialFilters={partialFilters}
        onChangeFilters={onChangeFilters}
        partialCount={partialCount}
        partialLoading={partialLoading}
      />
    </div>
  );

  return (
    <>
      <Head>
        <title>Student Societies | Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>

      <section
        className={styles.StudentSocieties}
        data-testid="StudentSocieties"
      >
        <div
          ref={headerRef}
          className={cn({
            [styles.header]: true,
            [styles.headerScrolled]: headerScrolled,
          })}
        >
          <div className={styles['header-content']}>
            <p className={styles.title}>
              {isRecruiter ? 'Student societies' : 'Explore societies'}
            </p>
            {headerScrolled ? (
              <Row columnGap={8}>
                {searchBox}
                <ButtonV3
                  color="secondary"
                  startIcon="filter-2"
                  onClick={() => setFilterClicked((flag) => !flag)}
                  className={cn({
                    [styles.filterClicked]: isFilterClicked,
                  })}
                >
                  Filters
                </ButtonV3>
              </Row>
            ) : (
              filterRow
            )}
          </div>
          {isFilterClicked && headerScrolled ? filterRow : null}
        </div>

        {isLoading ? (
          <Spinner size="medium" wrapper />
        ) : societies.length ? (
          <div className={styles.container}>
            {societies.map((society) => {
              const {
                id,
                icon_url,
                slug,
                name,
                university: universityResponse,
                is_verified,
                is_huzzle_verified,
              } = society;
              const university =
                parseResponse<UniversityAttributes>(universityResponse);

              return (
                <Col
                  key={id}
                  md={8}
                  lg={6}
                  xxl={5}
                  className={styles.StudentSocietiesCard}
                  onClick={() =>
                    router.push(
                      `${PAGE_ROUTES.STUDENT_SOCIETIES}/${slug}/detail`
                    )
                  }
                >
                  <div className={styles.avatarContainer}>
                    <Avatar name={name} icon={icon_url} size={'medium'} />
                    {is_verified || is_huzzle_verified ? (
                      <Icon
                        iconName={
                          is_huzzle_verified ? 'icon_star' : 'tick-mark-circle'
                        }
                        size="auto"
                        className={cn(
                          styles.icon,
                          is_huzzle_verified
                            ? styles.huzzleVerifiedIcon
                            : styles.verifiedIcon
                        )}
                      />
                    ) : null}
                  </div>
                  <div className={styles.details}>
                    <p className={styles.name}>{name}</p>
                    <p className={styles.nameUniversity}>{university?.name}</p>
                  </div>
                  <ButtonV3
                    size="small"
                    color="secondary"
                    isFullWidth
                    onClick={() =>
                      router.push(
                        `${PAGE_ROUTES.STUDENT_SOCIETIES}/${slug}/detail`
                      )
                    }
                  >
                    View
                  </ButtonV3>
                </Col>
              );
            })}
          </div>
        ) : (
          <Row
            className={styles.StudentSocietiesNoResults}
            align="center"
            justify="center"
          >
            <span>🙁</span>
            <p>No Results</p>
          </Row>
        )}
        <div ref={useBottomRef} className={styles.bottomElement}>
          {hasMoreRecords ? (
            <Spinner size="medium" wrapperClassname={styles.spinner} />
          ) : null}
        </div>
      </section>
    </>
  );
};

const StudentSocietiesWrapper = withAuth(StudentSocieties);

// @ts-ignore
StudentSocietiesWrapper.getLayout = getLayout;

export default StudentSocietiesWrapper;
